import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/Header';
import ManufacturerLogos from '@/components/ManufacturerLogos';
import Footer from '@/components/sections/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { User, Mail, Calendar, Plus, Package, Clock, CheckCircle, XCircle, DollarSign, Crown } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SellerDashboard() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading } = useAuth();
  const [listings, setListings] = useState([]);
  const [loadingListings, setLoadingListings] = useState(true);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (token) {
      fetchListings();
    }
  }, [token]);

  const fetchListings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/me/listings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setListings(response.data.listings || []);
    } catch (error) {
      console.error('Failed to fetch listings:', error);
    } finally {
      setLoadingListings(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary" className="gap-1"><Clock className="w-3 h-3" /> Under Review</Badge>;
      case 'pending_payment':
        return <Badge variant="secondary" className="gap-1 bg-yellow-100 text-yellow-800"><DollarSign className="w-3 h-3" /> Awaiting Payment</Badge>;
      case 'approved':
        return <Badge variant="default" className="gap-1 bg-green-600"><CheckCircle className="w-3 h-3" /> Live</Badge>;
      case 'sold':
        return <Badge className="gap-1 bg-blue-600"><DollarSign className="w-3 h-3" /> Sold</Badge>;
      case 'rejected':
        return <Badge variant="destructive" className="gap-1"><XCircle className="w-3 h-3" /> Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPrice = (price, currency) => {
    const symbols = { USD: '$', GBP: '£', EUR: '€' };
    const symbol = symbols[currency] || '$';
    return `${symbol}${price.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <ManufacturerLogos />
      
      <main className="flex-1 container mx-auto px-4 py-8" data-testid="seller-dashboard">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Welcome back, {user.name?.split(' ')[0]}!
          </h1>
          <p className="text-muted-foreground">
            Manage your listings and track your sales
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Profile Card */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold">{user.name}</p>
                    {user.seller_plan === 'Dealer_Pro' && (
                      <Badge className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white border-0 gap-1">
                        <Crown className="w-3 h-3" />
                        PRO DEALER
                      </Badge>
                    )}
                  </div>
                  <Badge variant="outline" className="mt-1 bg-orange-50 text-orange-700 border-orange-200">
                    Seller Account
                  </Badge>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span>{user.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span>Member since {formatDate(user.created_at)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats & Quick Actions */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>Manage your drone listings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Total Listings</p>
                  <p className="text-3xl font-bold">{listings.length}</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Under Review</p>
                  <p className="text-3xl font-bold">
                    {listings.filter(l => l.status === 'pending').length}
                  </p>
                </div>
              </div>
              
              <Button 
                onClick={() => navigate('/sell')}
                className="w-full h-auto py-4 flex items-center justify-center gap-2"
                data-testid="new-listing-btn"
              >
                <Plus className="w-5 h-5" />
                <span>Create New Listing</span>
              </Button>
            </CardContent>
          </Card>

          {/* Listings Section */}
          <Card className="lg:col-span-3">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Your Listings
              </CardTitle>
              <CardDescription>
                Track the status of your drone listings
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingListings ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : listings.length === 0 ? (
                <div className="text-center py-12 bg-muted/30 rounded-lg">
                  <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No listings yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Start selling your drones by creating your first listing
                  </p>
                  <Button onClick={() => navigate('/sell')} data-testid="create-first-listing-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Listing
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {listings.map((listing) => (
                    <div 
                      key={listing.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h4 className="font-semibold">
                            {listing.drone_brand} {listing.model_selling}
                          </h4>
                          {getStatusBadge(listing.status)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>{listing.condition}</span>
                          <span>•</span>
                          <span>{listing.drone_category}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Listed {formatDate(listing.created_at)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">
                          {formatPrice(listing.asking_price, listing.currency)}
                        </p>
                        <p className="text-xs text-muted-foreground">{listing.currency}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
}
